import { AllowFilterValueType, FilterItem, FilterOption, FilterValue, Sort } from '@mindly/ui-components';
import { TFunction } from 'i18next';
import { UserType } from 'src/pages/types';
import {
  AreaOfWorkByClientProblem,
  FilterObject,
  FiltersData,
  LocalizedAreaOfWorkByClientProblem,
} from 'src/shared/types/filters';
const LOCAL_STORAGE_KEY = 'Mindly-filters';
const LOCAL_STORAGE_KEY_SORT = 'Mindly-sort';

const getFieldWithLocaleSuffix = <T>(locale: string, key: keyof T, obj: T) => {
  switch (locale) {
    case 'uk':
      return obj[key];
    default:
      return obj[`${String(key)}_${locale}` as keyof T];
  }
};

const getLabel = (locale: string, obj: { label: string; label_es: string; label_pl: string }) => {
  return getFieldWithLocaleSuffix(locale, 'label', obj);
};

const getName = (locale: string, obj: { name: string; name_es: string; name_pl: string }) => {
  return getFieldWithLocaleSuffix(locale, 'name', obj);
};

const getPriceRange = (user: UserType) => {
  if (user.possible_price_filters) {
    const range = (user.possible_price_filters ?? []).map((price) => price / 100);
    const min = Math.min(...range);
    const max = Math.max(...range);
    return {
      min,
      max,
      step: (max - min) / (range.length - 1),
      range,
      multiple: false,
    };
  }
  switch (user.app_lang) {
    case 'uk':
      return {
        min: 500,
        max: 4000,
        step: 100,
      };
    case 'pl':
      return {
        min: 30,
        max: 400,
        step: 10,
      };
    case 'es':
      return {
        min: 10,
        max: 100,
        step: 10,
      };
    default:
      return {
        min: 10,
        max: 300,
        step: 10,
      };
  }
};

export const getFilters = (
  t: TFunction,
  presetFilters: Record<string, FilterValue>,
  user: UserType,
  filtersData: FiltersData
): FilterItem[] => {
  const locale = user.app_lang ?? 'uk';
  const priceRange = getPriceRange(user);
  const isLocalized = !filtersData.areasOfWorkByClientProblem.some((item) => 'name_es' in item || 'name_pl' in item);

  const getLocalizedItem = (
    child: AreaOfWorkByClientProblem['items'][number] | LocalizedAreaOfWorkByClientProblem['items'][number]
  ) => {
    return isLocalized
      ? { label: child.label, value: child.id }
      : {
          label: getLabel(locale, child as AreaOfWorkByClientProblem['items'][number]),
          value: getLabel(locale, child as AreaOfWorkByClientProblem['items'][number]),
        };
  };

  const popularAreasOfWorkByClientProblem = filtersData.areasOfWorkByClientProblem.reduce(
    (acc: FilterOption[], item) => {
      item.items.forEach((child) => {
        if (child.isFavorite) {
          acc.push(getLocalizedItem(child));
        }
      });
      return acc;
    },
    []
  );
  const multiple = typeof priceRange.multiple === 'boolean' ? priceRange.multiple : true;
  return [
    {
      key: 'session_price',
      type: 'range',
      multiple,
      label: t('ui:filters.price.label'),
      value:
        presetFilters.notIncludeMin && Array.isArray(presetFilters.session_price)
          ? presetFilters.session_price[1]
          : presetFilters.session_price,
      range: priceRange.range,
      props: {
        min: priceRange.min,
        max: priceRange.max,
        dualKnobs: multiple,
        snaps: true,
        step: priceRange.step,
      },
    },
    {
      key: 'sex',
      type: 'chips',
      label: t('ui:filters.sex.label'),
      value: presetFilters.sex,
      multiple: true,
      options: [
        { value: 'male', label: t('ui:filters.sex.male') },
        { value: 'female', label: t('ui:filters.sex.female') },
      ],
    },
    {
      key: 'year_of_birthday',
      type: 'range',
      multiple: true,
      label: t('ui:filters.age.label'),
      value: presetFilters.year_of_birthday,
      props: {
        min: 22,
        max: 55,
        dualKnobs: true,
        snaps: true,
        step: 1,
        currency: undefined,
      },
    },
    {
      key: 'specialization',
      type: 'chips',
      multiple: true,
      label: t('ui:filters.specialization.label'),
      value: presetFilters.specialization,
      options: filtersData.specialization
        .map((item) => ({
          value: getLabel(locale, item),
          label: getLabel(locale, item),
        }))
        .filter((item) => Boolean(item.value)),
      props: {
        limit: 8,
      },
    },
    {
      key: 'super_specialist',
      type: 'boolean',
      label: `🏆 ${t('ui:therapist_list_item.super_specialist.title')}`,
      description: `${t('ui:therapist_list_item.super_specialist.description')}`,
      value: presetFilters.super_specialist,
    },
    {
      key: 'activated_at',
      type: 'boolean',
      label: t('ui:filters.activated_at.label'),
      description: t('ui:filters.activated_at.description'),
      value: presetFilters.activated_at,
      badge: {
        children: 'New',
        size: 'S',
        variant: 'info',
      },
    },
    {
      key: 'areas_of_work',
      type: 'chips',
      multiple: true,
      label: t('ui:filters.area_of_work.label'),
      value: presetFilters.areas_of_work,
      options: filtersData.areasOfWork
        .map((item) => ({
          value: getLabel(locale, item),
          label: getLabel(locale, item),
        }))
        .filter((item) => Boolean(item.value)),
      props: {
        limit: 5,
      },
    },
    {
      key: isLocalized ? 'localized_areas_of_work_by_client_problem' : 'areas_of_work_by_client_problem',
      type: 'chips',
      multiple: true,
      label: t('ui:filters.help_with.label'),
      value: isLocalized
        ? presetFilters.localized_areas_of_work_by_client_problem
        : presetFilters.areas_of_work_by_client_problem,
      options: [
        ...(popularAreasOfWorkByClientProblem.length > 0
          ? [
              {
                label: t('ui:filters.help_with.popular'),
                children: popularAreasOfWorkByClientProblem,
              },
            ]
          : []),
        ...filtersData.areasOfWorkByClientProblem.map((item) => ({
          label: isLocalized ? item.name : getName(locale, item as AreaOfWorkByClientProblem),
          children: item.items
            .filter(
              (child) =>
                !popularAreasOfWorkByClientProblem.some((el) =>
                  isLocalized ? el.value === child.id : el.value === getLabel(locale, child as never)
                )
            )
            .map((child) => getLocalizedItem(child)),
        })),
      ],
      props: {
        limit: 5,
      },
    },
  ];
};

type FilterObjectEntry = {
  [K in keyof FilterObject]: [K, FilterObject[K]];
}[keyof FilterObject];

export const getPresetedFiltersValues = (filtersData: FiltersData, user: UserType): Record<string, FilterValue> => {
  const result: Record<string, FilterValue> = {};
  const priceRange = getPriceRange(user);
  Object.entries(filtersData.filterObject).forEach((it) => {
    const [key, value] = it as FilterObjectEntry;
    switch (key) {
      case 'age': {
        break;
      }
      case 'sex': {
        const filterValue = value.reduce((acc: AllowFilterValueType[], item) => {
          if (Array.isArray(item.filtration_values)) {
            acc = [...acc, ...item.filtration_values];
          } else {
            acc = [...acc, item.filtration_values];
          }
          return acc;
        }, []);
        result[key] = filterValue;
        break;
      }
      case 'session_price': {
        if (typeof value === 'number') {
          if (priceRange.range) {
            const closestPrevRange = [...priceRange.range].reverse().find((prev) => prev < value / 100);
            if (closestPrevRange) {
              result.notIncludeMin = true;
              result[key] = [closestPrevRange, value / 100];
            } else {
              result[key] = value / 100;
            }
          }
          break;
        }
        const maxFilterValue = value.find((item) => item.operator === '<=')?.filtration_values;
        const minFilterValue = value.find((item) => item.operator === '>=')?.filtration_values;
        if (typeof minFilterValue !== 'number' && typeof maxFilterValue !== 'number') break;
        if (typeof minFilterValue === 'number' && typeof maxFilterValue === 'number') {
          result[key] = [minFilterValue, maxFilterValue];
          break;
        }
        if (typeof minFilterValue === 'number') {
          result[key] = [minFilterValue, priceRange.max];
        }
        if (typeof maxFilterValue === 'number') {
          result[key] = [priceRange.min, maxFilterValue];
        }
        break;
      }
      case 'localized_areas_of_work_by_client_problem': {
        const filterValue = value.reduce((acc: AllowFilterValueType[], item) => {
          if (Array.isArray(item.filtration_values)) {
            acc = [...acc, ...item.filtration_values];
          } else {
            acc = [...acc, item.filtration_values];
          }
          return acc;
        }, []);
        result[key] = filterValue;
        break;
      }
      default: {
        const filterValue = value.reduce((acc: AllowFilterValueType[], item) => {
          if (Array.isArray(item.filtration_values)) {
            acc = [...acc, ...item.filtration_values];
          } else {
            acc = [...acc, item.filtration_values];
          }
          return acc;
        }, []);
        result[key] = filterValue;
      }
    }
  });
  return result;
};

const replacer = (key: string, value: any) => (value === undefined ? '__undefined__' : value);
const reviver = (key: string, value: any) => (value === '__undefined__' ? undefined : value);

const getFiltersKey = (locale?: string, userId?: string) =>
  `${LOCAL_STORAGE_KEY}${locale ? `_${locale}` : ''}${userId ? `_${userId}` : ''}`;

export const migrateSavedFilters = (locale: string, userId: string) => {
  const prev = getSavedFilters();
  if (Object.keys(prev).length > 0) {
    clearSavedFilters();
    saveFilters(prev, locale, userId);
  }
};

export const saveFilters = (filters: Record<string, FilterValue>, locale?: string, userId?: string) => {
  localStorage.setItem(getFiltersKey(locale, userId), JSON.stringify(filters, replacer));
};

export const getSavedFilters = (locale?: string, userId?: string): Record<string, FilterValue> => {
  return JSON.parse(localStorage.getItem(getFiltersKey(locale, userId)) ?? '{}', reviver);
};

export const clearSavedFilters = (locale?: string, userId?: string) => {
  localStorage.removeItem(getFiltersKey(locale, userId));
};

export const copyAndResetSavedFilters = (oldLocale?: string, newLocale?: string, userId?: string) => {
  const newLocaleFilters = getSavedFilters(newLocale, userId);
  if (Object.keys(newLocaleFilters).length > 0) {
    return;
  }
  const prev = getSavedFilters(oldLocale, userId);
  const newFilters = Object.entries(prev).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: Array.isArray(value) ? [] : undefined,
    }),
    {}
  );
  saveFilters(newFilters, newLocale, userId);
};

export const saveSort = (sort: Sort) => {
  localStorage.setItem(LOCAL_STORAGE_KEY_SORT, JSON.stringify(sort, replacer));
};

export const getSavedSort = (): Sort | undefined => {
  const value = localStorage.getItem(LOCAL_STORAGE_KEY_SORT);
  if (!value) return undefined;
  return JSON.parse(value, reviver);
};

export const clearSavedSort = () => {
  localStorage.removeItem(LOCAL_STORAGE_KEY_SORT);
};
